import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import type { Product } from "@/hooks/useProducts";

interface ProductCardProps {
  product: Product;
  onClick: () => void;
}

export const ProductCard = ({ product, onClick }: ProductCardProps) => {
  const discountedPrice = product.base_price * (1 - product.discount_percentage / 100);
  const hasDiscount = product.discount_percentage > 0;
  
  const minCondition = product.variants?.reduce((min, v) => {
    const grades = ['A+', 'A', 'B', 'C'];
    return grades.indexOf(v.condition_grade) > grades.indexOf(min) ? v.condition_grade : min;
  }, 'A+') || 'A';

  const totalQuantity = product.variants?.reduce((sum, v) => sum + v.quantity, 0) || 0;

  return (
    <Card 
      className="group cursor-pointer overflow-hidden border-border/50 hover:border-primary/30 transition-all duration-300 hover:shadow-lg hover:-translate-y-1"
      onClick={onClick}
    >
      <div className="relative aspect-square bg-muted overflow-hidden">
        {product.cover_image ? (
          <img 
            src={product.cover_image} 
            alt={product.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-muted-foreground">
            Nessuna immagine
          </div>
        )}
        
        {hasDiscount && (
          <Badge className="absolute top-3 left-3 bg-destructive text-destructive-foreground">
            -{product.discount_percentage}%
          </Badge>
        )}
        
        {totalQuantity === 0 && (
          <div className="absolute inset-0 bg-background/80 flex items-center justify-center">
            <span className="text-muted-foreground font-medium">Esaurito</span>
          </div>
        )}
      </div>
      
      <CardContent className="p-4">
        <h3 className="font-semibold text-foreground line-clamp-2 mb-2 group-hover:text-primary transition-colors">
          {product.name}
        </h3>
        
        <div className="flex items-center gap-2 mb-2">
          <Badge variant="secondary" className="text-xs">
            Grado {minCondition}
          </Badge>
          {product.category && (
            <Badge variant="outline" className="text-xs">
              {product.category.name}
            </Badge>
          )}
        </div>
        
        <div className="flex items-baseline gap-2">
          <span className="text-xl font-bold text-foreground">
            €{discountedPrice.toFixed(2)}
          </span>
          {hasDiscount && (
            <span className="text-sm text-muted-foreground line-through">
              €{product.base_price.toFixed(2)}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
